import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as Yup from "yup"
import { showAlert } from "store/reducers/alertReducer"
import { addUser, getUserDetails, updateUser } from "store/actions/userAction"
import { useNavigate } from "react-router-dom"
import {
  arabicEnglishDigitRegex,
  arabicEnglishDigitRegexTenDigit,
  arabicNameOrNumberPattern,
} from "components/Common/Utils"
import Spinners from "components/Shared/Spinners"
import {
  addTrainingMaterial,
  getTrainingMaterialDetails,
  updateTrainingMaterial,
} from "store/actions/trainingMaterialAction"
import BackButton from "components/Shared/BackButton"

const initialValues = {
  name: "",
  name_ar: "",
  contents: "",
  contents_ar: "",
}

const CreateTrainingMaterial = ({ mode }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const uuid = searchParams.get("uuid")

  const { isGettingDetails, isLoading, trainingMaterialDetails } = useSelector(
    state => state.TrainingMaterials
  )

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    const getDetails = () => {
      dispatch(getTrainingMaterialDetails(uuid))
    }
    mode === "update" && getDetails()
  }, [mode])

  useEffect(() => {
    if (!isGettingDetails && trainingMaterialDetails && mode === "update") {
      const { name, name_ar, contents, contents_ar } = trainingMaterialDetails
      setFormValues(prev => ({
        name,
        name_ar,
        contents,
        contents_ar,
      }))
    }
  }, [mode, trainingMaterialDetails])

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("Field is required"),
      name_ar: Yup.string()
        .required("Field is required")
        .matches(
          arabicNameOrNumberPattern,
          t("Only Arabic letters are allowed")
        ),
      contents: Yup.string().required("Field is required"),
      contents_ar: Yup.string().required("Field is required"),
    })

  const handleOnSubmit = values => {
    const { name, name_ar, contents, contents_ar } = values
    let data = new FormData()
    data.append("name", name)
    data.append("name_ar", name_ar)
    // Handle contents (English PDF)
    if (contents instanceof File) {
      data.append("contents", contents)
    }

    // Handle contents_ar (Arabic PDF)
    if (contents_ar instanceof File) {
      data.append("contents_ar", contents_ar)
    }
    if (mode === "add") {
      dispatch(addTrainingMaterial(data))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "update") {
      const updatedPayload = {
        uuid,
        data,
      }
      dispatch(updateTrainingMaterial(updatedPayload))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const handleFileChange = (event, setFieldValue, fieldName) => {
    const fileInput = event.currentTarget // Get the file input element
    const file = fileInput.files[0]

    if (file) {
      if (file.type !== "application/pdf") {
        dispatch(
          showAlert({ text: t("Only PDF files are allowed!"), type: "error" })
        )
        setFieldValue(fieldName, null)
        fileInput.value = "" // Clear file input
        return
      }

      if (file.size > 20 * 1024 * 1024) {
        // 20MB limit
        dispatch(
          showAlert({
            text: t("File size should not exceed 20MB!"),
            type: "error",
          })
        )
        setFieldValue(fieldName, null)
        fileInput.value = "" // Clear file input
        return
      }

      setFieldValue(fieldName, file)
    } else {
      setFieldValue(fieldName, null)
    }
  }

  const handleRemoveFile = (setFieldValue, fieldName) => {
    setFieldValue(fieldName, null)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {mode === "update" && isGettingDetails ? (
          <Spinners />
        ) : (
          <Container fluid>
            <h4 className="card-title ">
              <BackButton />
              {mode === "add"
                ? t("Add Training Material")
                : t("Update Training Material")}
            </h4>
            <Card className="theme_card">
              <CardBody>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={validationSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, errors, touched, setFieldValue }) => (
                    <Form>
                      <Row>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="name"
                              label={t("Name(English)")}
                              isRequired={true}
                            />
                            <CustomField
                              name="name"
                              type="text"
                              placeholder={t("Name(English)")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="name_ar"
                              label={t("Name(Arabic)")}
                              isRequired={true}
                            />
                            <CustomField
                              name="name_ar"
                              type="text"
                              placeholder={t("Name(Arabic)")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="contents"
                              label={t("Contents(English)")}
                              isRequired={true}
                            />
                            <input
                              id="contents"
                              name="contents"
                              type="file"
                              accept=".pdf"
                              className={`form-control ${
                                errors?.contents && touched?.contents
                                  ? "is-invalid"
                                  : ""
                              } `}
                              onChange={event =>
                                handleFileChange(
                                  event,
                                  setFieldValue,
                                  "contents"
                                )
                              }
                            />
                            <ErrorMessage
                              name={"contents"}
                              component="div"
                              className="text-danger"
                            />
                            {values.contents && (
                              <div className="mt-2">
                                <a
                                  href={
                                    typeof values.contents === "string"
                                      ? values.contents
                                      : URL.createObjectURL(values.contents)
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-link text-decoration-underline"
                                >
                                  {t("View PDF")}
                                </a>
                                {/* <button
                                  type="button"
                                  className="btn btn-danger btn-sm ms-2"
                                  onClick={() =>
                                    handleRemoveFile(setFieldValue, "contents")
                                  }
                                >
                                  Remove
                                </button> */}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="contents_ar"
                              label={t("Contents(Arabic)")}
                              isRequired={true}
                            />
                            <input
                              id="contents_ar"
                              name="contents_ar"
                              type="file"
                              accept=".pdf"
                              className={`form-control ${
                                errors?.contents_ar && touched?.contents_ar
                                  ? "is-invalid"
                                  : ""
                              } `}
                              onChange={event =>
                                handleFileChange(
                                  event,
                                  setFieldValue,
                                  "contents_ar"
                                )
                              }
                            />
                            {/* Show file link & remove button if a file is selected */}
                            {values.contents_ar && (
                              <div className="mt-2">
                                <a
                                  href={
                                    typeof values.contents_ar === "string"
                                      ? values.contents_ar
                                      : URL.createObjectURL(values.contents_ar)
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-link text-decoration-underline"
                                >
                                  {t("View PDF")}
                                </a>
                                {/* <button
                                  type="button"
                                  className="btn btn-danger btn-sm ms-2"
                                  onClick={() =>
                                    handleRemoveFile(
                                      setFieldValue,
                                      "contents_ar"
                                    )
                                  }
                                >
                                  Remove
                                </button> */}
                              </div>
                            )}
                            <ErrorMessage
                              name={"contents_ar"}
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="g_end mt-2 gap-3 cis_btn">
                            <button
                              className="main_btn disabled-button fit_content px-4"
                              type="button"
                              onClick={() => navigate(-1)}
                              disabled={isLoading}
                            >
                              {t("Cancel")}
                            </button>
                            <button
                              className="main_btn fit_content px-4"
                              type="submit"
                              disabled={isLoading}
                            >
                              {mode === "add" ? t("Create") : t("Update")}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Container>
        )}
      </div>
    </React.Fragment>
  )
}

export default CreateTrainingMaterial
